import React, { useState, useEffect, useContext } from "react";
import { ethers } from "ethers";
import {
  Box,
  Center,
  HStack,
  Spinner,
  Text,
  VStack,
  Button,
  Spacer,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { AppContext } from "../../AppContext";
import JakobFactory from "../../contracts/WalletFactoryArbitrum.json";
import JakobWallet from "../../contracts/JakobWallet.json";
import { formatAddress } from "../../utils/formatMetamask";
import CopyToClipboardButton from "../../utils/CopyToClipboardButton";
import { link, uni, usdc, usdc_e, wbtc, weth } from "../../config";

const DeployedWallets = () => {
  const [deployedWallets, setDeployedWallets] = useState([]);
  const [showDetails, setShowDetails] = useState(false);
  const [loading, setLoading] = useState(false);
  const { fastestWebSocket, setContractAddress } = useContext(AppContext);
  const provider = new ethers.providers.WebSocketProvider(fastestWebSocket);
  const walletFactoryContract = new ethers.Contract(
    JakobFactory.address,
    JakobFactory.abi,
    provider,
  );

  const USDC_E_ADDRESS = usdc_e;
  const USDC_ADDRESS = usdc;
  const LINK_ADDRESS = link;
  const UNI_ADDRESS = uni;
  const BTC_ADDRESS = wbtc;
  const WETH_ADDRESS = weth;

  const fetchNativeBalance = async (walletAddress) => {
    const balance = await provider.getBalance(walletAddress);
    return ethers.utils.formatUnits(balance, 18);
  };

  const fetchTokenBalance = async (tokenAddress, walletAddress) => {
    const tokenContract = new ethers.Contract(
      tokenAddress,
      ["function balanceOf(address owner) view returns (uint256)"],
      provider,
    );
    const balance = await tokenContract.balanceOf(walletAddress);
    return ethers.utils.formatUnits(
      balance,
      tokenAddress === USDC_ADDRESS ? 6 : tokenAddress === BTC_ADDRESS ? 8 : 18,
    );
  };

  const fetchOwnersFromWallet = async (walletAddress) => {
    const walletContract = new ethers.Contract(
      walletAddress,
      JakobWallet.abi,
      provider,
    );
    const [owner1, owner2, owner3, paymaster] = await Promise.all([
      walletContract.owner1(),
      walletContract.owner2(),
      walletContract.owner3(),
      walletContract.paymaster(),
    ]);
    return { owner1, owner2, owner3, paymaster };
  };

  const fetchDeployedWallets = async () => {
    try {
      setLoading(true);
      const wallets = await walletFactoryContract.getDeployedWallets();

      const updatedWallets = await Promise.all(
        wallets.map(async (wallet) => {
          const nativeBalance = await fetchNativeBalance(wallet.walletAddress);
          const usdcBalance = await fetchTokenBalance(
            USDC_ADDRESS,
            wallet.walletAddress,
          );
          const linkBalance = await fetchTokenBalance(
            LINK_ADDRESS,
            wallet.walletAddress,
          );
          const btcBalance = await fetchTokenBalance(
            BTC_ADDRESS,
            wallet.walletAddress,
          );
          const wethBalance = await fetchTokenBalance(
            WETH_ADDRESS,
            wallet.walletAddress,
          );
          const owners = await fetchOwnersFromWallet(wallet.walletAddress);

          return {
            ...wallet,
            ...owners,
            nativeBalance,
            usdcBalance,
            linkBalance,
            btcBalance,
            wethBalance,
          };
        }),
      );

      setDeployedWallets(updatedWallets);
    } catch (error) {
      console.error("Error fetching deployed wallets:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeployedWallets();
  }, []);

  // Toggle the visibility of details for a specific wallet
  const toggleDetails = (index) => {
    setDeployedWallets((prevWallets) =>
      prevWallets.map((wallet, i) =>
        i === index ? { ...wallet, showDetails: !wallet.showDetails } : wallet,
      ),
    );
  };

  return (
    <Box fontFamily="Sentinel">
      <Text mb={2} fontWeight="bold">
        Arbitrum Accounts
      </Text>

      {loading ? (
        <Center mt={8}>
          <VStack>
            <Spinner />
            <Text>Loading accounts...</Text>
          </VStack>
        </Center>
      ) : deployedWallets.length > 0 ? (
        deployedWallets.map((wallet, index) => {
          return (
            <Box
              key={index}
              p={1}
              bg="white"
              w="100%"
              shadow='dark-lg'
              fontFamily={"Sentinel"}
              borderWidth="1px"
              borderColor="dodgerblue"
              borderRadius="md"
              mt={2}
              mb={2}
            >
              {/* Top row: Contract Name, Wallet Address, Toggle Button */}
              <HStack w="100%" alignItems="center">
                {/* Contract Name */}
                <Box flex="1.7" minW="59%">
                  <Tooltip
                    label={wallet.contractName}
                    aria-label="Contract Name"
                  >
                    <Text as="b" fontSize="sm" isTruncated noOfLines={1}>
                      {wallet.contractName}
                    </Text>
                  </Tooltip>
                </Box>

                {/* Wallet Address and Copy Button */}
                <HStack gap="1px" flex="2" justifyContent="flex-end">
                  <Text fontSize="sm" isTruncated>
                    <a
                      href={`https://arbiscan.io/address/${wallet.walletAddress}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {formatAddress(wallet.walletAddress)}
                    </a>
                  </Text>
                  <CopyToClipboardButton value={wallet.walletAddress} />
                </HStack>

                {/* Toggle Details Button */}
                <IconButton
                  icon={
                    wallet.showDetails ? <ChevronUpIcon /> : <ChevronDownIcon />
                  }
                  aria-label="Toggle Details"
                  onClick={() => toggleDetails(index)}
                  variant="outline"
                  size='xs'
                  colorScheme="blue"
                />
              </HStack>

              {/* Details section */}
              {wallet.showDetails && (
                <VStack align="start" spacing={2} mt={1} fontSize="sm">
                  <HStack w="100%">
                    <Text>
                      <strong>Name:</strong>{" "}
                    </Text>
                    <Text as="b">{wallet.contractName}</Text>
                  </HStack>

                  <HStack w="100%">
                    <Text>
                      <strong>Account</strong>{" "}
                    </Text>
                    {/* Wallet Address and Copy Button */}
                    <Text isTruncated>
                      <a
                        href={`https://arbiscan.io/address/${wallet.walletAddress}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {formatAddress(wallet.walletAddress)}
                      </a>
                    </Text>
                    <CopyToClipboardButton value={wallet.walletAddress} />
                  </HStack>
                  <HStack w="100%">
                    <Text>
                      <strong>Owner1:</strong>{" "}
                      <a
                        href={`https://arbiscan.io/address/${wallet.owner1}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {formatAddress(wallet.owner1)}
                      </a>
                    </Text>
                    <Spacer />
                    <CopyToClipboardButton value={wallet.owner1} />
                  </HStack>

                  <HStack w="100%">
                    <Text>
                      <strong>Owner2:</strong>{" "}
                      <a
                        href={`https://arbiscan.io/address/${wallet.owner2}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {formatAddress(wallet.owner2)}
                      </a>
                    </Text>
                    <Spacer />
                    <CopyToClipboardButton value={wallet.owner2} />
                  </HStack>

                  <HStack w="100%">
                    <Text>
                      <strong>Owner3:</strong>{" "}
                      <a
                        href={`https://arbiscan.io/address/${wallet.owner3}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {formatAddress(wallet.owner3)}
                      </a>
                    </Text>
                    <Spacer />
                    <CopyToClipboardButton value={wallet.owner3} />
                  </HStack>

                  <HStack w="100%">
                    <Text>
                      <strong>Spender:</strong>{" "}
                      <a
                        href={`https://arbiscan.io/address/${wallet.paymaster}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {formatAddress(wallet.paymaster)}
                      </a>
                    </Text>
                    <Spacer />
                    <CopyToClipboardButton value={wallet.paymaster} />
                  </HStack>

                  {/* Wallet Balances */}
                  {parseFloat(wallet.nativeBalance) > 0 && (
                    <Text>
                      <strong>ETH Balance:</strong>{" "}
                      {parseFloat(wallet.nativeBalance).toFixed(5)} ETH
                    </Text>
                  )}
                  {parseFloat(wallet.usdcBalance) > 0 && (
                    <Text>
                      <strong>USDC Balance:</strong>{" "}
                      {parseFloat(wallet.usdcBalance).toFixed(2)} USDC
                    </Text>
                  )}
                  {parseFloat(wallet.linkBalance) > 0 && (
                    <Text>
                      <strong>LINK Balance:</strong>{" "}
                      {parseFloat(wallet.linkBalance).toFixed(3)} LINK
                    </Text>
                  )}
                  {parseFloat(wallet.btcBalance) > 0 && (
                    <Text>
                      <strong>BTC Balance:</strong>{" "}
                      {parseFloat(wallet.btcBalance).toFixed(5)} BTC
                    </Text>
                  )}
                  {parseFloat(wallet.wethBalance) > 0 && (
                    <Text>
                      <strong>WETH Balance:</strong>{" "}
                      {parseFloat(wallet.wethBalance).toFixed(5)} WETH
                    </Text>
                  )}
                </VStack>
              )}

              {/* Load Account Button */}
              <Button
                color="white"
                bg="#0051ff"
                size="sm"
                borderRadius="md"
                w="100%"
                mt={2}
                onClick={() => setContractAddress(wallet.walletAddress)}
              >
                Load Account
              </Button>
            </Box>
          );
        })
      ) : (
        <Text>No wallets deployed yet.</Text>
      )}
    </Box>
  );
};

export default DeployedWallets;
